/*
 * La Via della Seta - theme.js
 * JS vanilla ES6, nessuna dipendenza, IIFE, caricato con defer.
 * Implementa tutti gli hook del contratto. Robusto e idempotente:
 * non genera errori se un elemento del DOM e assente.
 *
 * Convenzioni di delega: gli handler che agiscono su elementi rigenerati
 * dal Section Rendering API (carrello) sono delegati su `document`.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Configurazione globale (con fallback robusti)                      */
  /* ------------------------------------------------------------------ */

  // window.theme viene popolato da theme.liquid; usiamo fallback prudenti.
  var theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || {};
  theme.strings = theme.strings || {};
  theme.settings = theme.settings || {};

  var ROUTES = {
    cartAdd: theme.routes.cart_add || '/cart/add.js',
    cartChange: theme.routes.cart_change || '/cart/change.js',
    cart: theme.routes.cart_get || '/cart.js'
  };

  var STRINGS = {
    addToCart: theme.strings.addToCart || 'Aggiungi al carrello',
    adding: theme.strings.adding || 'Aggiunta...',
    soldOut: theme.strings.soldOut || 'Esaurito',
    unavailable: theme.strings.unavailable || 'Non disponibile'
  };

  // Formato valuta Shopify (es. "&euro;{{amount}}" oppure "{{amount}} &euro;").
  var MONEY_FORMAT = theme.moneyFormat || '&euro;{{amount}}';

  // Nome della sezione del carrello da rigenerare via Section Rendering API.
  var CART_SECTION = 'cart-drawer';

  /* ------------------------------------------------------------------ */
  /* Utility                                                            */
  /* ------------------------------------------------------------------ */

  /** querySelector sicuro su un root (default document). */
  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  /** querySelectorAll come array reale. */
  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  /** Risale dall'evento al primo antenato che matcha il selettore (incluso target). */
  function closestMatch(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  /** Config standard per fetch verso le route AJAX di Shopify. */
  function fetchConfig(body) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    };
  }

  /** Path corrente, usato come sections_url per il Section Rendering API. */
  function getSectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  /** Rispetto delle preferenze utente / impostazioni tema sulle animazioni. */
  function prefersReducedMotion() {
    var rootReduced =
      document.documentElement.classList.contains('reduced-motion') ||
      theme.settings.animationsEnabled === false;
    var mediaReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return rootReduced || mediaReduced;
  }

  /**
   * Formattatore di prezzo semplice in centesimi EUR.
   * Usa MONEY_FORMAT con i placeholder Shopify {{amount}} / {{amount_no_decimals}}.
   */
  function formatMoney(cents) {
    cents = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = thousands || '.';
      decimal = decimal || ',';
      var amount = (number / 100.0).toFixed(precision);
      var parts = amount.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
      switch (token) {
        case 'amount':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_space_separator':
          return withDelimiters(cents, 2, ' ', ',');
        default:
          return withDelimiters(cents, 2, '.', ',');
      }
    });
  }

  /** Esegue una callback senza errori; logga in console in debug. */
  function safe(fn) {
    try {
      return fn();
    } catch (err) {
      if (window.console && console.warn) console.warn('[theme]', err);
      return undefined;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cart count                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Copia il valore di [data-cart-count-source] (rigenerato nel drawer)
   * in tutti i [data-cart-count]. Se non c'e una source, usa un valore esplicito.
   */
  function updateCartCount(explicitCount) {
    var count = explicitCount;
    if (typeof count !== 'number') {
      var source = qs('[data-cart-count-source]');
      if (source) {
        count = parseInt(source.textContent.trim(), 10);
      }
    }
    if (typeof count !== 'number' || isNaN(count)) return;

    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      // Mostra/nasconde un badge "bolla" se previsto dal markup.
      el.hidden = count === 0 && el.hasAttribute('data-cart-count-hide-empty');
      el.classList.toggle('is-empty', count === 0);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer: apertura / chiusura                                   */
  /* ------------------------------------------------------------------ */

  var lastCartTrigger = null; // per ripristinare il focus alla chiusura

  function getDrawer() {
    return qs('[data-cart-drawer]');
  }

  function openCartDrawer(trigger) {
    var drawer = getDrawer();
    if (!drawer) {
      // Nessun drawer in pagina: degradiamo verso la pagina carrello.
      window.location.href = ROUTES.cart.replace(/\.js$/, '');
      return;
    }
    if (trigger) lastCartTrigger = trigger;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open', 'scroll-locked');

    // Focus sul pannello dialog per l'accessibilita.
    var panel = qs('[role="dialog"]', drawer) || drawer;
    safe(function () {
      panel.focus({ preventScroll: true });
    });
  }

  function closeCartDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open', 'scroll-locked');

    // Ritorno del focus al trigger che ha aperto il drawer.
    if (lastCartTrigger && typeof lastCartTrigger.focus === 'function') {
      safe(function () {
        lastCartTrigger.focus();
      });
    }
    lastCartTrigger = null;
  }

  /**
   * Sostituisce il contenuto interno del drawer con l'HTML reso dal
   * Section Rendering API, poi aggiorna i conteggi.
   */
  function renderCartSection(sectionHTML) {
    var drawer = getDrawer();
    if (!drawer || typeof sectionHTML !== 'string') return;

    // Estraiamo dall'HTML della sezione il solo contenuto interno se presente.
    var parser = new DOMParser();
    var doc = parser.parseFromString(sectionHTML, 'text/html');
    var newInner = doc.querySelector('[data-cart-drawer-inner]');
    var currentInner = qs('[data-cart-drawer-inner]', drawer);

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
    } else if (currentInner) {
      // Fallback: la risposta non aveva il wrapper, usiamo tutto il body utile.
      var bodyInner = doc.querySelector('[data-cart-drawer]') || doc.body;
      currentInner.innerHTML = bodyInner ? bodyInner.innerHTML : sectionHTML;
    } else {
      // Nessun wrapper: rimpiazziamo l'intero contenuto del drawer.
      drawer.innerHTML = sectionHTML;
    }

    updateCartCount();
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart (form prodotto, card e quick-add)                      */
  /* ------------------------------------------------------------------ */

  /** Mostra un messaggio di errore vicino al form, se possibile. */
  function showFormError(form, message) {
    if (!message) return;
    var target = form ? qs('[data-product-form-error]', form) : null;
    if (target) {
      target.textContent = message;
      target.hidden = false;
    } else if (window.console && console.warn) {
      console.warn('[theme] cart error:', message);
    }
  }

  function clearFormError(form) {
    var target = form ? qs('[data-product-form-error]', form) : null;
    if (target) {
      target.textContent = '';
      target.hidden = true;
    }
  }

  /**
   * POST /cart/add.js con i campi sezione, rirender del drawer e apertura.
   * Ritorna la Promise per consentire ai chiamanti di concatenare stati UI.
   */
  function postAdd(body, trigger) {
    body.sections = CART_SECTION;
    body.sections_url = getSectionsUrl();

    return fetch(ROUTES.cartAdd, fetchConfig(body))
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          // Shopify ritorna {status, message, description} sugli errori.
          var msg = result.data && (result.data.description || result.data.message);
          throw new Error(msg || STRINGS.unavailable);
        }
        if (result.data && result.data.sections && result.data.sections[CART_SECTION]) {
          renderCartSection(result.data.sections[CART_SECTION]);
        } else {
          // Senza sezione, almeno aggiorniamo il conteggio via /cart.js.
          refreshCartCount();
        }
        openCartDrawer(trigger);
        return result.data;
      });
  }

  /** API pubblica semplice: aggiunge una variante per id. */
  function addToCart(id, qty, trigger) {
    if (!id) return Promise.reject(new Error('missing variant id'));
    return postAdd({ id: id, quantity: qty && qty > 0 ? qty : 1 }, trigger).catch(
      function (err) {
        if (window.console && console.warn) console.warn('[theme]', err.message);
      }
    );
  }

  /** Recupera il conteggio dal /cart.js (fallback senza Section Rendering). */
  function refreshCartCount() {
    return fetch(ROUTES.cart, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          updateCartCount(cart.item_count);
        }
      })
      .catch(function () {});
  }

  /** Intercetta il submit di tutti i form prodotto. */
  function onProductFormSubmit(event) {
    var form = closestMatch(event.target, 'form.product-form');
    if (!form) return;

    event.preventDefault();
    clearFormError(form);

    var button = qs('[data-add-to-cart]', form);
    var originalLabel = button ? button.innerHTML : '';

    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
      button.classList.add('is-loading');
      var label = qs('[data-add-to-cart-text]', button);
      if (label) {
        label.textContent = STRINGS.adding;
      } else {
        button.dataset.originalLabel = originalLabel;
        button.textContent = STRINGS.adding;
      }
    }

    // Costruiamo il body a partire dai campi del form (incluse le proprieta).
    var formData = new FormData(form);
    var body = {};
    formData.forEach(function (value, key) {
      // Le proprieta del form prodotto usano la sintassi properties[Nome].
      var propMatch = key.match(/^properties\[(.+)\]$/);
      if (propMatch) {
        body.properties = body.properties || {};
        body.properties[propMatch[1]] = value;
      } else if (key === 'quantity') {
        body.quantity = parseInt(value, 10) || 1;
      } else if (key === 'id') {
        body.id = value;
      } else if (key !== 'form_type' && key !== 'utf8') {
        body[key] = value;
      }
    });

    function restoreButton() {
      if (!button) return;
      button.removeAttribute('aria-disabled');
      button.disabled = false;
      button.classList.remove('is-loading');
      var lbl = qs('[data-add-to-cart-text]', button);
      if (lbl) {
        lbl.textContent = STRINGS.addToCart;
      } else if (button.dataset.originalLabel) {
        button.innerHTML = button.dataset.originalLabel;
        delete button.dataset.originalLabel;
      }
    }

    postAdd(body, button)
      .then(function () {
        restoreButton();
      })
      .catch(function (err) {
        restoreButton();
        showFormError(form, err && err.message);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Quick-add da card                                                  */
  /* ------------------------------------------------------------------ */

  function closeAllQuickAddPanels(except) {
    qsa('[data-quick-add-panel]').forEach(function (panel) {
      if (panel !== except) {
        panel.hidden = true;
        panel.classList.remove('is-open');
        var owner = panel.closest('[data-quick-add-wrapper]') || panel.parentNode;
        var toggle = owner ? qs('[data-quick-add]', owner) : null;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function onQuickAddClick(event) {
    // 1) Click su una variante dentro il popover.
    var variantBtn = closestMatch(event.target, '[data-variant-id]');
    if (variantBtn && variantBtn.hasAttribute('data-variant-id') && closestMatch(variantBtn, '[data-quick-add-panel]')) {
      if (variantBtn.getAttribute('data-available') === 'false') return; // ignora esaurite
      var vid = variantBtn.getAttribute('data-variant-id');
      addToCart(vid, 1, variantBtn).then(function () {
        closeAllQuickAddPanels();
      });
      return;
    }

    // 2) Click sul bottone quick-add della card.
    var trigger = closestMatch(event.target, '[data-quick-add]');
    if (!trigger) return;

    // Monovariante: aggiunge subito.
    var directId = trigger.getAttribute('data-variant-id');
    if (directId) {
      addToCart(directId, 1, trigger);
      return;
    }

    // Multivariante: apre/chiude il popover associato.
    var wrapper = trigger.closest('[data-quick-add-wrapper]') || trigger.parentNode;
    var panel = wrapper ? qs('[data-quick-add-panel]', wrapper) : null;
    if (!panel) return;

    var willOpen = panel.hidden;
    closeAllQuickAddPanels(willOpen ? panel : null);
    panel.hidden = !willOpen;
    panel.classList.toggle('is-open', willOpen);
    trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------ */
  /* Quantita e rimozione nel drawer (delega su document)               */
  /* ------------------------------------------------------------------ */

  /** POST /cart/change.js con rirender della sezione drawer. */
  function changeLine(line, quantity) {
    var body = {
      line: line,
      quantity: quantity,
      sections: CART_SECTION,
      sections_url: getSectionsUrl()
    };
    return fetch(ROUTES.cartChange, fetchConfig(body))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data && data.sections && data.sections[CART_SECTION]) {
          renderCartSection(data.sections[CART_SECTION]);
        } else {
          updateCartCount(data ? data.item_count : undefined);
        }
        return data;
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('[theme]', err);
      });
  }

  function onCartMutationClick(event) {
    // Rimozione riga.
    var removeBtn = closestMatch(event.target, '[data-cart-remove]');
    if (removeBtn) {
      event.preventDefault();
      var rLine = parseInt(removeBtn.getAttribute('data-line'), 10);
      if (rLine) changeLine(rLine, 0);
      return;
    }

    // Incremento / decremento quantita.
    var plus = closestMatch(event.target, '[data-cart-qty-plus]');
    var minus = closestMatch(event.target, '[data-cart-qty-minus]');
    if (!plus && !minus) return;

    var btn = plus || minus;
    var line = parseInt(btn.getAttribute('data-line'), 10);
    if (!line) return;

    // Troviamo l'input quantita associato alla riga.
    var input =
      qs('[data-cart-qty][data-line="' + line + '"]') ||
      (btn.parentNode ? qs('[data-cart-qty]', btn.parentNode) : null);

    var current = input ? parseInt(input.value, 10) || 0 : 0;
    var next = plus ? current + 1 : current - 1;
    if (next < 0) next = 0;

    if (input) input.value = next;
    changeLine(line, next);
  }

  /** Modifica diretta dell'input quantita nel drawer (cambio manuale). */
  function onCartQtyInputChange(event) {
    var input = closestMatch(event.target, '[data-cart-qty]');
    if (!input) return;
    var line = parseInt(input.getAttribute('data-line'), 10);
    if (!line) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    input.value = qty;
    changeLine(line, qty);
  }

  /* ------------------------------------------------------------------ */
  /* Reveal (IntersectionObserver)                                      */
  /* ------------------------------------------------------------------ */

  function initReveal() {
    var elements = qsa('.reveal');
    if (!elements.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });

    // Rete di sicurezza: se l'observer non scatta (rendering sospeso,
    // browser particolari), il contenuto non deve restare invisibile.
    setTimeout(function () {
      elements.forEach(function (el) {
        el.classList.add('is-visible');
      });
    }, 2000);

    // Salviamo l'observer per eventuale uso futuro / debug.
    theme._revealObserver = observer;
  }

  /* ------------------------------------------------------------------ */
  /* Header: scroll state + menu mobile                                 */
  /* ------------------------------------------------------------------ */

  function initHeader() {
    var header = qs('[data-header]');
    if (header) {
      var onScroll = function () {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  var lastMenuTrigger = null;

  function openMenuDrawer(toggle) {
    var drawer = qs('[data-menu-drawer]');
    if (!drawer) return;
    lastMenuTrigger = toggle || lastMenuTrigger;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open', 'scroll-locked');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    var focusable = qs('[data-menu-close]', drawer) || drawer;
    safe(function () {
      focusable.focus({ preventScroll: true });
    });
  }

  function closeMenuDrawer() {
    var drawer = qs('[data-menu-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open', 'scroll-locked');

    qsa('[data-menu-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    if (lastMenuTrigger && typeof lastMenuTrigger.focus === 'function') {
      safe(function () {
        lastMenuTrigger.focus();
      });
    }
    lastMenuTrigger = null;
  }

  /* ------------------------------------------------------------------ */
  /* Galleria prodotto                                                  */
  /* ------------------------------------------------------------------ */

  function onGalleryThumbClick(event) {
    var thumb = closestMatch(event.target, '[data-gallery-thumb]');
    if (!thumb) return;

    var gallery = thumb.closest('[data-gallery]');
    if (!gallery) return;

    var main = qs('[data-gallery-main]', gallery);
    if (!main) return;

    var fullSrc = thumb.getAttribute('data-full-src');
    var fullSrcset = thumb.getAttribute('data-full-srcset');
    if (fullSrc) main.setAttribute('src', fullSrc);
    if (fullSrcset) {
      main.setAttribute('srcset', fullSrcset);
    } else if (fullSrc) {
      main.removeAttribute('srcset');
    }

    var alt = thumb.getAttribute('data-full-alt');
    if (alt !== null) main.setAttribute('alt', alt);

    // Aggiorna lo stato aria-current sui thumb.
    qsa('[data-gallery-thumb]', gallery).forEach(function (t) {
      t.removeAttribute('aria-current');
      t.classList.remove('is-active');
    });
    thumb.setAttribute('aria-current', 'true');
    thumb.classList.add('is-active');
  }

  /** Imposta l'immagine principale della galleria da un media-id (usato dalle varianti). */
  function setGalleryMainByMedia(media) {
    if (!media) return;
    var main = qs('[data-gallery-main]');
    if (!main) return;

    var src =
      (media.preview_image && media.preview_image.src) || media.src || null;
    if (!src) return;

    // Costruiamo un src con width per coerenza con image_url.
    var width = main.getAttribute('width') || 1000;
    var sized = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    main.setAttribute('src', sized);
    main.removeAttribute('srcset');

    // Sincronizza lo stato dei thumb se presente il media-id.
    var gallery = main.closest('[data-gallery]');
    if (gallery && media.id != null) {
      qsa('[data-gallery-thumb]', gallery).forEach(function (t) {
        var isMatch = String(t.getAttribute('data-media-id')) === String(media.id);
        t.toggleAttribute('aria-current', isMatch);
        if (isMatch) {
          t.setAttribute('aria-current', 'true');
          t.classList.add('is-active');
        } else {
          t.removeAttribute('aria-current');
          t.classList.remove('is-active');
        }
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Variant picker                                                     */
  /* ------------------------------------------------------------------ */

  /** Legge il JSON prodotto associato al form. */
  function readProductJSON(form) {
    var script = qs('[data-product-json]', form) || qs('[data-product-json]');
    if (!script) return null;
    return safe(function () {
      return JSON.parse(script.textContent);
    });
  }

  /** Raccoglie i valori opzione selezionati dentro un variant picker. */
  function getSelectedOptions(picker) {
    var options = [];

    // Select native.
    qsa('select[data-variant-input]', picker).forEach(function (select) {
      var idx = parseInt(select.getAttribute('data-option-index'), 10);
      options[isNaN(idx) ? options.length : idx] = select.value;
    });

    // Gruppi di radio: prendiamo il checked per ciascun data-option-index.
    var radios = qsa('input[type="radio"][data-variant-input]:checked', picker);
    radios.forEach(function (radio) {
      var idx = parseInt(radio.getAttribute('data-option-index'), 10);
      options[isNaN(idx) ? options.length : idx] = radio.value;
    });

    return options;
  }

  /** Trova la variante che corrisponde alle opzioni selezionate. */
  function findVariant(product, selectedOptions) {
    if (!product || !product.variants) return null;
    return (
      product.variants.filter(function (variant) {
        return selectedOptions.every(function (value, index) {
          if (value == null) return true;
          return variant.options[index] === value;
        });
      })[0] || null
    );
  }

  function updateVariantUI(form, product, variant) {
    // Campo nascosto name="id".
    var idInput = qs('input[name="id"]', form);
    if (idInput) {
      idInput.value = variant ? variant.id : '';
    }

    // Prezzo. Il blocco prezzo e fratello del form (blocchi 2.0): fallback a livello document.
    var priceTarget = qs('[data-price-target]', form) || qs('[data-price-target]');
    if (priceTarget) {
      if (variant) {
        var html = '<span class="price__regular">' + formatMoney(variant.price) + '</span>';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html =
            '<span class="price__sale">' +
            formatMoney(variant.price) +
            '</span> <s class="price__compare">' +
            formatMoney(variant.compare_at_price) +
            '</s>';
        }
        priceTarget.innerHTML = html;
      }
    }

    // Stato del bottone add-to-cart.
    var button = qs('[data-add-to-cart]', form);
    if (button) {
      var label = qs('[data-add-to-cart-text]', button);
      var available = variant && variant.available;
      button.disabled = !available;
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
      var text = !variant
        ? STRINGS.unavailable
        : available
        ? STRINGS.addToCart
        : STRINGS.soldOut;
      if (label) {
        label.textContent = text;
      } else {
        button.textContent = text;
      }
    }

    // Immagine principale della galleria dalla featured_media della variante.
    if (variant && variant.featured_media) {
      setGalleryMainByMedia(variant.featured_media);
    }

    // Aggiorna l'URL con ?variant= per condivisione (senza reload).
    if (variant && window.history && window.history.replaceState) {
      safe(function () {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      });
    }
  }

  function onVariantChange(event) {
    var input = closestMatch(event.target, '[data-variant-input]');
    if (!input) return;
    var picker = input.closest('[data-variant-picker]');
    if (!picker) return;
    var form = picker.closest('.product-form') || qs('.product-form');
    if (!form) return;

    var product = readProductJSON(form);
    if (!product) return;

    var selected = getSelectedOptions(picker);
    var variant = findVariant(product, selected);
    updateVariantUI(form, product, variant);
  }

  /** Inizializza lo stato UI delle varianti al load (coerenza con ?variant=). */
  function initVariantPickers() {
    qsa('.product-form').forEach(function (form) {
      var picker = qs('[data-variant-picker]', form);
      if (!picker) return;
      var product = readProductJSON(form);
      if (!product) return;
      var selected = getSelectedOptions(picker);
      var variant = findVariant(product, selected);
      if (variant) updateVariantUI(form, product, variant);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Quantita in pagina prodotto                                        */
  /* ------------------------------------------------------------------ */

  function onQtyButtonClick(event) {
    var plus = closestMatch(event.target, '[data-qty-plus]');
    var minus = closestMatch(event.target, '[data-qty-minus]');
    if (!plus && !minus) return;

    var btn = plus || minus;
    var wrapper = btn.closest('[data-qty]') || btn.parentNode;
    var input = wrapper ? qs('[data-qty-input]', wrapper) : qs('[data-qty-input]');
    if (!input) return;

    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;
    var current = parseInt(input.value, 10) || min;
    var next = plus ? current + 1 : current - 1;
    if (next < min) next = min;
    input.value = next;

    // Notifica eventuali listener (es. validazioni).
    safe(function () {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Size guide (dialog nativo)                                         */
  /* ------------------------------------------------------------------ */

  function onSizeGuideOpen(event) {
    var trigger = closestMatch(event.target, '[data-size-guide-open]');
    if (!trigger) return;
    var dialog = qs('[data-size-guide]');
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      safe(function () {
        dialog.showModal();
      });
    } else {
      // Fallback per browser senza <dialog>.
      dialog.setAttribute('open', '');
    }
  }

  function onSizeGuideClose(event) {
    var trigger = closestMatch(event.target, '[data-size-guide-close]');
    if (trigger) {
      var dialog = trigger.closest('[data-size-guide]') || qs('[data-size-guide]');
      if (dialog) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      return;
    }

    // Click sul backdrop del dialog: se il target e l'elemento <dialog> stesso
    // (area backdrop, non il contenuto interno), chiudiamo.
    var dialogEl = closestMatch(event.target, 'dialog[data-size-guide]');
    if (dialogEl && event.target === dialogEl) {
      if (typeof dialogEl.close === 'function') dialogEl.close();
      else dialogEl.removeAttribute('open');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Localizzazione (auto-submit)                                       */
  /* ------------------------------------------------------------------ */

  function onLocalizationChange(event) {
    var select = closestMatch(event.target, '.localization-form select');
    if (!select) return;
    var form = select.closest('.localization-form');
    if (form && typeof form.submit === 'function') {
      form.submit();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Gestione tasto Escape (drawer, menu, popover)                      */
  /* ------------------------------------------------------------------ */

  function onKeyDown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;

    var drawer = getDrawer();
    if (drawer && drawer.classList.contains('is-open')) {
      closeCartDrawer();
      return;
    }
    var menu = qs('[data-menu-drawer]');
    if (menu && menu.classList.contains('is-open')) {
      closeMenuDrawer();
      return;
    }
    // I quick-add popover aperti si chiudono con ESC.
    var openPanel = qs('[data-quick-add-panel].is-open');
    if (openPanel) {
      closeAllQuickAddPanels();
    }
    // Il <dialog> della size guide gestisce ESC nativamente.
  }

  /* ------------------------------------------------------------------ */
  /* Click globale: dispatcher (delega)                                 */
  /* ------------------------------------------------------------------ */

  function onDocumentClick(event) {
    // Apertura carrello.
    var cartToggle = closestMatch(event.target, '[data-cart-toggle]');
    if (cartToggle) {
      event.preventDefault();
      openCartDrawer(cartToggle);
      return;
    }
    // Chiusura carrello.
    if (closestMatch(event.target, '[data-cart-close]')) {
      event.preventDefault();
      closeCartDrawer();
      return;
    }
    // Overlay carrello.
    if (closestMatch(event.target, '[data-cart-overlay]')) {
      closeCartDrawer();
      return;
    }

    // Menu mobile.
    var menuToggle = closestMatch(event.target, '[data-menu-toggle]');
    if (menuToggle) {
      event.preventDefault();
      var menu = qs('[data-menu-drawer]');
      if (menu && menu.classList.contains('is-open')) closeMenuDrawer();
      else openMenuDrawer(menuToggle);
      return;
    }
    if (closestMatch(event.target, '[data-menu-close]') || closestMatch(event.target, '[data-menu-overlay]')) {
      closeMenuDrawer();
      return;
    }

    // Quantita pagina prodotto.
    if (closestMatch(event.target, '[data-qty-plus]') || closestMatch(event.target, '[data-qty-minus]')) {
      onQtyButtonClick(event);
      return;
    }

    // Quantita / rimozione carrello (delega: il drawer si rigenera).
    if (
      closestMatch(event.target, '[data-cart-qty-plus]') ||
      closestMatch(event.target, '[data-cart-qty-minus]') ||
      closestMatch(event.target, '[data-cart-remove]')
    ) {
      onCartMutationClick(event);
      return;
    }

    // Galleria.
    if (closestMatch(event.target, '[data-gallery-thumb]')) {
      onGalleryThumbClick(event);
      return;
    }

    // Size guide.
    if (closestMatch(event.target, '[data-size-guide-open]')) {
      onSizeGuideOpen(event);
      return;
    }
    if (closestMatch(event.target, '[data-size-guide-close]')) {
      onSizeGuideClose(event);
      return;
    }

    // Quick-add (card e popover varianti).
    if (
      closestMatch(event.target, '[data-quick-add]') ||
      (closestMatch(event.target, '[data-variant-id]') && closestMatch(event.target, '[data-quick-add-panel]'))
    ) {
      onQuickAddClick(event);
      return;
    }

    // Click fuori da un popover quick-add aperto: chiudilo.
    if (!closestMatch(event.target, '[data-quick-add-wrapper]')) {
      var hasOpen = qs('[data-quick-add-panel].is-open');
      if (hasOpen) closeAllQuickAddPanels();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Bind globali                                                       */
  /* ------------------------------------------------------------------ */

  function bindGlobalEvents() {
    // Submit dei form prodotto (delega: card e pagina prodotto).
    document.addEventListener('submit', onProductFormSubmit, false);

    // Click dispatcher unico.
    document.addEventListener('click', onDocumentClick, false);

    // Backdrop del <dialog> size guide (click diretto sull'elemento dialog).
    document.addEventListener('click', onSizeGuideClose, false);

    // Cambi: varianti, quantita drawer, localizzazione.
    document.addEventListener('change', function (event) {
      if (closestMatch(event.target, '[data-variant-input]')) {
        onVariantChange(event);
      } else if (closestMatch(event.target, '[data-cart-qty]')) {
        onCartQtyInputChange(event);
      } else if (closestMatch(event.target, '.localization-form select')) {
        onLocalizationChange(event);
      }
    });

    // Tastiera (Escape).
    document.addEventListener('keydown', onKeyDown, false);
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    bindGlobalEvents();
    initReveal();
    initHeader();
    initVariantPickers();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Esponi alcune API utili per le sezioni e per il debug.
  theme.openCartDrawer = openCartDrawer;
  theme.closeCartDrawer = closeCartDrawer;
  theme.addToCart = addToCart;
  theme.formatMoney = formatMoney;
  theme.updateCartCount = updateCartCount;
  theme.refreshCartCount = refreshCartCount;
})();
